import { getFormattedDurationForStoryVideo } from '@src/utility/timeFormat';

import { clipIdentifiedSpeakers } from './ReelEditor/clipPickerFilter';

import type { DatoSpeaker, OtherVideo } from '@src/types.ts/story';

export type ClipSelectionSummary = {
  /** e.g. `"01:12"` — how long the reel these picks would make runs. */
  duration: string;
  /**
   * The hover line: the people the picks record, and — only when the picks are
   * a mix — what they are made of. e.g. `"2 speakers · 2 clips · 1 reel"`.
   */
  detail: string;
};

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * `7:40` — a running total, not a timecode, so the minutes lose the padding
 * `convertTimeToMMSS` gives a clip's own badge. Reading how long a selection
 * runs is reading a number; "07:40" makes it look like a position in a video.
 */
const runningTime = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/**
 * What the picked clips add up to, for the bulk bar: how long they run, how
 * many people they record, and what they are made of. The creator gets that
 * without adding up the cards' duration badges or reading their face piles.
 *
 * Each part is best-effort and comes back empty when it cannot be stated:
 * speakers need the story's labels resolved to people (the same
 * {@link clipIdentifiedSpeakers} the cards' face piles use, so the bar counts
 * exactly the faces on screen — the clip's *predominant* speakers, not everyone
 * heard), and a clip with no known duration contributes nothing to the total.
 * Saying "0 speakers" or "00:00" for a selection we simply have no metadata for
 * would be worse than saying nothing.
 *
 * The clip/reel split is stated only for a mixed selection: an all-clips pick
 * already says its own count in the bar ("3 selected"), so repeating it as
 * "3 clips" is noise — the split is worth reading exactly when the picks are
 * not all one kind. Reel-ness is `clipJson.isReel`, the same rule the Type
 * filter reads (PLAT-11128), not the card's tag copy.
 */
export function clipSelectionSummary(
  clips: readonly OtherVideo[],
  speakersByLabel: ReadonlyMap<string, DatoSpeaker> | undefined,
): ClipSelectionSummary {
  const speakerIds = new Set<string>();
  let seconds = 0;
  let reels = 0;

  for (const clip of clips) {
    clipIdentifiedSpeakers(clip, speakersByLabel).forEach(speaker =>
      speakerIds.add(speaker.id),
    );
    seconds += getFormattedDurationForStoryVideo(clip).duration ?? 0;
    if (clip.clipJson?.isReel) reels++;
  }

  const plainClips = clips.length - reels;
  const parts: string[] = [];
  if (speakerIds.size > 0) parts.push(plural(speakerIds.size, 'speaker'));
  if (reels > 0 && plainClips > 0) {
    parts.push(plural(plainClips, 'clip'), plural(reels, 'reel'));
  }

  return {
    duration: seconds > 0 ? runningTime(seconds) : '',
    detail: parts.join(' · '),
  };
}
